/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { Params, RouterModule } from '@angular/router';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { PictureComponent } from '@app/shared/components/picture/picture.component';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable, of, Subject, Subscription, takeUntil, tap } from 'rxjs';
import { ProductJsonLdComponent } from '../product-json-ld/product-json-ld.component';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { InformationCircleIconComponent } from '@app/shared/components/icons/generated/information-circle/information-circle.component';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';

import { Insurance } from '@app/akita/api/products/models/insurance.model';
import { Check1IconComponent } from '@app/shared/components/icons/generated/check-1/check-1.component';
import { PopsyCheckboxComponent } from '@app/components/checkbox/popsy-checkbox.component';
import { InsuranceOverlayDynamicService } from '@app/overlays/insurance-overlay/services/insurance-overlay-dynamic.service';
import { PopsyShield4IconComponent } from '@app/shared/components/icons/generated/popsy-shield-4/popsy-shield-4.component';
import { ChevronDownIconComponent } from '@app/shared/components/icons/generated/chevron-down/chevron-down.component';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';

@Component({
  selector: 'app-product-insurance-options',
  templateUrl: './product-insurance-options.template.html',
  styleUrls: ['./product-insurance-options.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    CommonModule,
    AmountPipe,
    TranslocoModule,
    FlexLayoutModule,
    RouterModule,
    PictureComponent,
    ProductJsonLdComponent,
    PopsySkeletonComponent,
    InformationCircleIconComponent,
    Check1IconComponent,
    PopsyCheckboxComponent,
    PopsyShield4IconComponent,
    ChevronDownIconComponent,
  ],
})
export class ProductInsuranceOptionsComponent implements OnChanges, OnDestroy {
  @Input() public id?: string | null;

  @Input() public model?: string | null;
  @Input() public category?: string | null;

  @Input() public showLabel?: boolean | null = true;

  @ViewChild('conditionButton', { read: ElementRef })
  public conditionButton?: ElementRef | null;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public language: string;

  public readonly selectedInsurance: WritableSignal<Insurance | null> = signal(null);

  public insuranceIds?: Array<string> | null;

  public insurancesOptions$: Observable<Array<Insurance | null>>;
  public insuranceExpanded: Array<boolean> = [];

  public findingInsurances$: Observable<boolean>;

  public readonly queryParamsToKeep$: Observable<Params>;

  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly insuranceOverlayDynamicService: InsuranceOverlayDynamicService
  ) {
    this.subscriptions = new Subscription();
    this.destroySubject = new Subject();

    this.language = this.akitaProductsQuery.validateLocale().language;

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.insurancesOptions$ = of([]);

    this.findingInsurances$ = of(false);

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.id) {
      this.refresh();
    }
  }

  private refresh(): void {
    // fetch in background
    this.subscriptions.add(
      this.akitaProductsQuery
        .selectProduct(this.id)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (product) => {
            if (
              product &&
              !product.insurances?.length &&
              this.akitaRouterQuery.isBrowser
            ) {
              this.zone.runOutsideAngular(() => {
                this.subscriptions.add(
                  this.akitaProductsService.fetchProductInsuranceAsync(product)
                );
              });
              this.insuranceIds = product.insuranceIds;
            }
          },
        })
    );

    this.insurancesOptions$ = this.akitaProductsQuery
      .selectProductInsurance(this.insuranceIds || [])
      .pipe(
        tap((insurances) => {
          this.insuranceExpanded = [];
          insurances?.forEach(() => this.insuranceExpanded.push(false));
        })
      );

    this.findingInsurances$ = this.akitaProductsQuery.selectIsFetchingInsurance(
      this.insuranceIds || []
    );

    this.changeDetector.markForCheck();
  }

  public onShowConditionInfo(): boolean {
    this.subscriptions.add(
      this.insuranceOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: () => {},
        })
    );

    this.insuranceOverlayDynamicService.open({
      data: {},
      isBrowser: this.akitaRouterQuery.isBrowser,
    });

    SentryUtil.addBreadcrumb({
      category: 'ui',
      message: `Opened "Language Overlay" modal`,
      level: 'info',
      type: 'user',
    });

    return false;
  }

  public isVariantSelected(condition: string): boolean {
    const currentVariant = this.akitaProductsQuery.getProductVariant(this.id, this.model);
    return currentVariant?.condition === condition && currentVariant?.inStock === true;
  }

  public onInsuranceSelected($event: boolean, insurance: Insurance | null): void {
    this.selectedInsurance.set($event ? insurance : null);

    this.akitaProductsService.addSelectedInsuranceToProduct(
      this.id || '',
      this.selectedInsurance()
    );

    this.changeDetector.markForCheck();
    this.changeDetector.detectChanges();
  }

  public isInsuranceSelected(insurance: Insurance | null): boolean {
    return Boolean(insurance?.id === this.selectedInsurance()?.id) || false;
  }

  public onExpandInsurance($event: any, index: number): void {
    $event.preventDefault();
    $event.stopPropagation();
    this.insuranceExpanded[index] = !this.insuranceExpanded[index];
    this.changeDetector.markForCheck();
  }

  public trackByInsurance(index: number, value?: any | null): string {
    return `${value?.id || index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
